// Admin "Clip Review" tab — human half of the automated clip review pipeline
// (services/clipReview.js on the backend runs the scrape + compliance +
// fraud-signal check and auto-approves clean submissions). Flagged
// submissions — failed compliance, a high fraud score, or a pipeline error —
// land here for a person to approve or reject. Reuses the .ref-*/.admindash-*
// classes from ReferralsTab so both admin queues look/feel the same.
import { useEffect, useState } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";

const STATUS_META = {
  flagged: { label: "Needs review", color: "#60a5fa" },
  pending: { label: "Reviewing", color: "#fbbf24" },
  approved: { label: "Approved", color: "#34d399" },
  rejected: { label: "Rejected", color: "#f87171" },
};
const FILTERS = ["flagged", "pending", "approved", "rejected"];
const PLATFORM_LABEL = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube" };

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

const COMPLIANCE_FIELDS = [
  ["captionOk", "Caption"],
  ["bioOk", "Bio"],
  ["usernameOk", "Username"],
  ["pfpOk", "Profile pic"],
];

export default function ClipReviewTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("flagged");
  const [decidingId, setDecidingId] = useState(null);

  const load = async (status = filter) => {
    try {
      const res = await axiosInstance.get(`/admin/clip-submissions?status=${status}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to load clip submissions");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(filter); }, [filter]);

  const decide = async (id, status) => {
    setDecidingId(id);
    try {
      await axiosInstance.post(`/admin/clip-submissions/${id}/status`, { status });
      toast.success(status === "approved" ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Couldn't update status");
    } finally {
      setDecidingId(null);
    }
  };

  if (!data) return <div className="admindash-empty admindash-shimmer">Loading…</div>;

  const { submissions = [], stats = {} } = data;

  return (
    <div className="admindash-section admindash-fade-in">
      <div className="ref-head">
        <div>
          <h2 className="admindash-section-title" style={{ margin: 0 }}>Clip review</h2>
          <div className="muted" style={{ fontSize: 13 }}>
            Automated compliance + fraud-signal check on every submitted clip · flagged ones need a human call
          </div>
        </div>
      </div>

      <div className="ref-filters">
        {FILTERS.map((s) => (
          <button
            key={s}
            className={`ref-pill ${filter === s ? "active" : ""}`}
            style={filter === s ? { borderColor: STATUS_META[s].color, color: STATUS_META[s].color } : undefined}
            onClick={() => setFilter(s)}
          >
            {STATUS_META[s].label} <span className="ref-pill-n">{stats[s] ?? 0}</span>
          </button>
        ))}
      </div>

      {submissions.length === 0 ? (
        <div className="admindash-empty">No {STATUS_META[filter].label.toLowerCase()} submissions.</div>
      ) : (
        <div className="ref-list">
          {submissions.map((s) => (
            <div key={s._id} className="ref-card">
              <div className="ref-card-top">
                <div className="ref-card-id">
                  <a href={s.url} target="_blank" rel="noreferrer" className="ref-handle">
                    {PLATFORM_LABEL[s.platform] || s.platform}
                  </a>
                  <span className="muted"> · @{s.user?.username || "unknown"}</span>
                  <span className="muted"> · {s.campaign?.title || "campaign"} ({s.campaign?.brandName})</span>
                </div>
                <span className="ref-status" style={{ color: STATUS_META[s.status]?.color }}>
                  {STATUS_META[s.status]?.label || s.status}
                </span>
              </div>

              <div className="ref-meta">
                <span>{fmtNum(s.scrape?.viewCount)} views</span>
                {s.scrape?.likeCount != null && <span>· {fmtNum(s.scrape.likeCount)} likes</span>}
                {s.scrape?.followerCount != null && <span>· {fmtNum(s.scrape.followerCount)} followers</span>}
                {s.payoutAmount > 0 && <span>· ${s.payoutAmount.toFixed(2)} payout</span>}
              </div>

              {s.scrape?.caption && (
                <div className="ref-why">"{s.scrape.caption.slice(0, 200)}{s.scrape.caption.length > 200 ? "…" : ""}"</div>
              )}

              {(s.compliance?.captionOk != null) && (
                <div className="ref-meta">
                  {COMPLIANCE_FIELDS.map(([key, label]) => (
                    <span key={key} style={{ color: s.compliance[key] ? "#34d399" : "#f87171" }}>
                      {s.compliance[key] ? "✓" : "✗"} {label}
                    </span>
                  ))}
                </div>
              )}
              {s.compliance?.notes && <div className="ref-why muted">Compliance: {s.compliance.notes}</div>}

              {s.fraudSignal?.score != null && s.fraudSignal.score > 0 && (
                <div className="ref-why muted">
                  Fraud signal ({s.fraudSignal.score}/100) — heuristic only, verify before rejecting:{" "}
                  {s.fraudSignal.reasons?.join("; ")}
                </div>
              )}

              {s.reviewError && <div className="ref-why muted">Review error: {s.reviewError}</div>}
              {s.reviewNotes && <div className="ref-why muted">Reviewer note: {s.reviewNotes}</div>}

              {(s.status === "flagged" || s.status === "pending") && (
                <div className="ref-actions">
                  <button
                    className="ref-btn ref-btn--sm"
                    disabled={decidingId === s._id}
                    onClick={() => decide(s._id, "approved")}
                  >
                    → Approve
                  </button>
                  <button
                    className="ref-btn ref-btn--sm"
                    disabled={decidingId === s._id}
                    onClick={() => decide(s._id, "rejected")}
                  >
                    → Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
